import { Field, LinkField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import { useI18n } from 'next-localization';
import { useEffect, useContext, useRef, useState, ChangeEventHandler, MouseEventHandler } from 'react';

import { ButtonVariant } from 'components/helpers/Button/Button';
import { contextType, HeaderContext, CustomerType } from './Header';
import Link from 'components/helpers/Link/Link';
import Select, { SelectHandle, type SelectOption } from 'components/helpers/Select/Select';
import styles from './Header.module.scss';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';

const cx = classNames.bind(styles);

type UtilityNavFlyOutProps = {
  index: number;
  userTypeDropdownLabel: Field<string>;
  customerTypes: CustomerType[];
  usernameLabel: Field<string>;
  buttonText: Field<string>;
  errorTextForDropdown: Field<string>;
  errorTextForUsername: Field<string>;
  mobileDestination: LinkField;
  mobileDestinationLabel: Field<string>;
};

const UtilityNavFlyOut = (props: UtilityNavFlyOutProps): React.JSX.Element | null => {
  const { t } = useI18n();
  const { openFlyout, setShowFlyout, currentLang, datasourceLanguage } = useContext(HeaderContext) as contextType;

  const [selected, setSelected] = useState<number>(0);
  const [madeChoice, setMadeChoice] = useState(false);
  const [selectError, setSelectError] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [username, setUsername] = useState('');

  const usernameRef = useRef<HTMLInputElement>(null);
  const custTypeRef = useRef<SelectHandle>(null);

  const custTypeLabels: SelectOption[] = props.customerTypes.map((customer) => {
    return { value: customer?.fields?.dropdownLabel?.value ?? '' };
  });

  const pickType: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setSelected(props.customerTypes.findIndex((customer) => customer.fields.dropdownLabel.value === e.target.value));
    setMadeChoice(true);
  };

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: Event) => {
      if (!ref.current?.contains(event.target as Element)) {
        setShowFlyout(false);
        window.removeEventListener('mousedown', handleOutsideClick);
      }
    };

    window.addEventListener('mousedown', handleOutsideClick);
  }, [ref, setShowFlyout, openFlyout]);

  const updateUsername: ChangeEventHandler<HTMLInputElement> = (e) => {
    const inputVal = e.target.value;
    if (!inputVal.trim()) {
      // not allowed: only white-spaces
      setUsername(e.target.value.trim());
    } else {
      setUsername(e.target.value);
    }
  };

  const CheckAndGo: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    let errors = false;

    if (username === '' || username.includes(' ')) {
      setUsernameError(true);
      errors = true;
      usernameRef.current?.focus();
    } else {
      setUsernameError(false);
    }

    if (!madeChoice) {
      setSelectError(true);
      errors = true;
      custTypeRef.current?.focus();
    } else {
      setSelectError(false);
    }

    if (!errors) {
      window.location.href = props.customerTypes[selected].fields.portalDestinationLink.value.href + username;
    }
  };

  return (
    <div className={cx('flyoutContainer')} ref={ref}>
      <Link
        field={props.mobileDestination}
        showLinkTextWithChildrenPresent={false}
        lang={datasourceLanguage ?? currentLang}
        onClick={(e) => {
          e.preventDefault();
          setShowFlyout(!openFlyout);
        }}
        className={cx('flyoutLink')}
      >
        {t('SignIn-NavFlyoutSignInLabel') == '' ? 'Sign In/Register' : t('SignIn-NavFlyoutSignInLabel')}
      </Link>
      <div className={cx('flyout')} hidden={!openFlyout}>
        <span className={cx('selectionBox')}>
          <Select
            checkSelectedOption={true}
            id="customerType"
            label={props.userTypeDropdownLabel.value}
            labelPosition="top"
            options={custTypeLabels}
            onChange={pickType}
            ref={custTypeRef}
          />
        </span>
        {selectError && (
          <label className={cx('error')} role="alert" htmlFor="customerType">
            {props.errorTextForDropdown.value}
          </label>
        )}
        {!selectError && <br />}
        <label htmlFor="userName">{props.usernameLabel.value}</label>
        <br />
        <input
          type="text"
          id="userName"
          autoComplete="username"
          value={username}
          onChange={updateUsername}
          ref={usernameRef}
        />
        {usernameError && (
          <label role="alert" htmlFor="userName" className={cx('error')}>
            {props.errorTextForUsername.value}
          </label>
        )}
        {!usernameError && (
          <>
            <br />
            <br />
          </>
        )}
        <div>
          <Link
            variant={ButtonVariant.Primary}
            lang={datasourceLanguage ?? currentLang}
            field={props.customerTypes[selected].fields.portalDestinationLink}
            showLinkTextWithChildrenPresent={false}
            className={cx('loginButton')}
            onClick={CheckAndGo}
          >
            {props.buttonText.value}
          </Link>
        </div>
        <div className={cx('links')}>
          <RichTextWrapper field={props.customerTypes[selected].fields.forgotPasswordShortInstructions} tag="p" />
          <Link
            field={props.customerTypes[selected].fields.forgotPasswordLink}
            lang={datasourceLanguage ?? currentLang}
            //hidden={props.customerTypes[selected].fields.forgotPasswordShortInstructions.value !== ''}
            editable={false}
          >
            {t('Forgot-Password')}
          </Link>
          <Link
            field={props.customerTypes[selected].fields.registerLink}
            showLinkTextWithChildrenPresent={false}
            lang={datasourceLanguage ?? currentLang}
          >
            <Text field={props.customerTypes[selected].fields.registerLinkText} encode={false} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UtilityNavFlyOut;
